import {type ChangeEvent, type FormEvent, useEffect, useRef, useState} from "react";
import MenuAiVoice from "../../component/menu.ai.voice.tsx";
import type {DaoCategory} from "../../../daos/dao-category.ts";
import type {DaoApiAiVoice} from "../../../daos/dao-api-ai-voice.ts";
import type {DaoMedia} from "../../../daos/dao-media.ts";
import {Api} from "../../../modules/api.ts";
import type {DaoVoice} from "../../../daos/dao-voice.ts";
import {AiVoice} from "../../component/ai.voice.tsx";
import type {ApiResponse} from "../../../modules/interfaces.ts";
import type {DaoApiBase} from "../../../daos/dao-api-base.ts";
import {Utils} from "../../../modules/utils.ts";
import {app, onair} from "../../../modules/config.ts";
import _ from "lodash";
import {useAppDispatch} from "../../../stores/hook.ts";
import {askAlert, askConfirm} from "../../../stores/dialog.thunk.ts";
import type {DaoApiAiVoiceCreate} from "../../../daos/dao-api-ai-voice-create.ts";


function PageAiVoice() {
  const dispatch = useAppDispatch();

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const historyRef = useRef<HTMLDivElement | null>(null);
  const [menu, setMenu] = useState<DaoCategory>();

  const [text, setText] = useState<string>('');
  const [textVoice, setTextVoice] = useState<string>('');
  const [textCount, setTextCount] = useState<number>(0);


  const [aiVoices, setAiVoices] = useState<DaoMedia[]>([]);
  // const [page, setPage] = useState<number>(0);
  // const [pageMax, setPageMax] = useState<number>(1);
  // useEffect(() => {
  //   const scroller = historyRef.current;
  //   let isProgress = false;
  //   let lastScrollTop = 0;
  //   const handleScroll = () => {
  //     if(scroller) {
  //       const scrollTop = scroller.scrollTop;
  //       if (Math.abs(scrollTop - lastScrollTop) <= 5) return;
  //       if (scrollTop > lastScrollTop) {
  //         if (!isProgress && scrollTop + scroller.clientHeight >= scroller.scrollHeight - 20) {
  //           if(pageMax > page) {
  //             isProgress = true;
  //             aiVoiceHistory(page + 1).then(rs => {
  //               setAiVoices(prev => [...prev, ...rs]);
  //               setTimeout(() => isProgress = false, 200);
  //             });
  //           }
  //         }
  //       }
  //       lastScrollTop = scrollTop;
  //     }
  //   };
  //   if(scroller) scroller.addEventListener('scroll', handleScroll);
  //   return () => {
  //     if(scroller) scroller.removeEventListener('scroll', handleScroll);
  //   }
  // }, [page, pageMax]);

  useEffect(() => {
    if(menu) {
      switch (menu.no) {
        case "history":
          setText('');
          setTextCount(0);
          aiVoiceHistory().then(rs => {
            setAiVoices(rs);
          });
          break;
        case "announce":
        default:
          // setPage(0);
          // setPageMax(1);
          setAiVoices([]);
          break;
      }
    }
  }, [menu]);

  useEffect(() => {
    if(_.isEmpty(textVoice)) {
      if(app.voices.length > 0) {
        const defVoice: DaoVoice = app.voices[0];
        if(defVoice) setTextVoice(defVoice.id);
      }
    }
  }, [textVoice]);

  function aiVoiceMenuHandler(menu: DaoCategory) {
    setAiVoices([]);
    setMenu(menu);
  }

  function textVoiceUpdate(voice: string): void {
    setTextVoice(voice);
  }

  function textVoiceChangeHandler(e: ChangeEvent<HTMLInputElement>) {
    textVoiceUpdate(e.currentTarget.value);
  }

  function textUpdate(text: string = '') {
    const length = Utils.strLength(text);
    if(length > 2000) {
      textUpdate(
        Utils.strCut(text, 2000)
      );
      return;
    }

    setText(text);
    setTextCount(length);
  }

  const textInputHandler = (e: FormEvent<HTMLTextAreaElement>) => {
    textUpdate(e.currentTarget.value);
  };

  function textClearHandler() {
    textUpdate('');
  }

  async function textAnnounceHandler() {
    if(_.isEmpty(textVoice)) {
      await dispatch(askAlert('', '목소리를 선택하세요.'));
      return;
    }

    if(Utils.strLength(text) < 20) {
      await dispatch(askAlert('', '한글 10자, 영문 20자 이상 입력해 주세요.'));
      return;
    }

    const isConfirm = await dispatch(askConfirm('', 'Ai 음성을 재생하시겠습니까?'));
    if(isConfirm) {
      const rs: ApiResponse<DaoApiAiVoiceCreate>  = await Api.ai_voice_create(textVoice, text);
      const daoApiAiVoiceCreate: DaoApiAiVoiceCreate = rs.data;
      if(daoApiAiVoiceCreate) {
        const media: DaoMedia = daoApiAiVoiceCreate.voice;
        if(media) {
          textUpdate('');
          listenHandler(media);
        }
      }
    }
  }

  async function aiVoiceHistory(p: number = 1): Promise<DaoMedia[]> {
    const rs: ApiResponse<DaoApiAiVoice> = await Api.ai_voice(p);
    if(rs.statusText === 'OK' && rs.data.rs === 1) {
      const daoApiAiVoice: DaoApiAiVoice = rs.data as DaoApiAiVoice;
      // setPage(daoApiAiVoice.page);
      // setPageMax(daoApiAiVoice.page_max);
      return daoApiAiVoice.items;
    }
    return [];
  }

  function listenHandler(history: DaoMedia): void {
    if(history) {
      const start_ms = onair.millisecond + 1000;
      const duration: number = history.duration + 1;
      onair.setSchedule(
        onair.getPriority('TTS'),
        [
          Object.assign({...history}, {
            duration,
            start_base: Utils.toDateString(Utils.now(start_ms)),
            start: Utils.toDateString(Utils.now(start_ms)),
            end: Utils.toDateString(Utils.now(start_ms + (duration * 1000)))
          })
        ]
      );
    }
  }

  function copyHandler(history: DaoMedia): void {
    if(history) {
      setMenu({no: 'announce', name: '바로 재생'});
      textUpdate(history.title);
    }
  }

  async function deleteHandler(history: DaoMedia): Promise<void> {
    if(history) {
      const rs: ApiResponse<DaoApiBase> = await Api.ai_voice_delete(history.media);
      if(rs.statusText === 'OK' && rs.data.rs === 1) {
        setAiVoices(prev => prev.filter((voice: DaoMedia) => voice.media !== history.media));
      }
    }
  }

  return (
    <main>
      <section className="contents">
        <MenuAiVoice callback={aiVoiceMenuHandler} menu={menu} />
        {
          menu?.no === 'announce' ?
            (
              <div className="ai-generator">
                <div className="voice-options">
                  {(app.voices ?? []).map((voice: DaoVoice, voice_idx: number) => (
                    <label key={voice_idx}>
                      <input type="radio" name="tone" value={voice.id}
                             checked={textVoice === voice.id}
                             onChange={textVoiceChangeHandler}/>{voice.name}
                    </label>
                  ))}
                </div>
                <div className="textarea-wrapper">
                  <textarea ref={inputRef}
                            placeholder="AI음성으로 재생할 스크립트를 입력하세요."
                            onInput={textInputHandler}
                            value={text}></textarea>
                  <div className="char-guide">
                    한글 10자, 영문 20자 이상 입력해 주세요.
                    <div className="char-counter"><span id="charCount">{textCount}</span>/2000</div>
                  </div>
                </div>

                <div className="btn-group">
                  <button id="clearBtn" onClick={textClearHandler}>내용 지우기</button>
                  <button id="playBtn" onClick={textAnnounceHandler} disabled={textCount < 20}>AI음성 재생</button>
                </div>
              </div>
            ) :
            (
              <div className="announce-list">
                <div className="scroll-gradient-top"></div>
                <div className="scroll-gradient-bottom"></div>
                <div className="ai-history" ref={historyRef}>
                  {
                    aiVoices.length < 1 ?
                      <article className="channel-item">
                        "AI 음성" 재생 내역이 없습니다.
                      </article> :
                      aiVoices.map((aiVoice: DaoMedia) => (
                        <AiVoice key={aiVoice.media}
                                 aivoice={aiVoice}
                                 callbackListen={listenHandler}
                                 callbackCopy={copyHandler}
                                 callbackDelete={deleteHandler} />
                      ))
                  }
                </div>
              </div>
            )
        }
      </section>
    </main>
  )
}

export default PageAiVoice;